import React from "react";
import {
  Box, Typography, Chip, Stack,
  Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import theme from "../../theme/theme";
import {
  HealthCoverageAnalysis,
  HealthCoverageLayerGroup,
  HealthCoverageItem,
} from "../../interfaces/DTO/insurance_data/HealthCoverageAnalysis";
import { formatHandlerCode, formatCurrency } from "../../utils/namesFormater";
import { formatPlanClassification } from "../../utils/insuranceUtil";
import { PlanClassification } from "../../interfaces/DTO/insurance_data/InsuranceData";
import {
  formatDuplicateRisk,
  formatCoverageItemLabel,
  subCoverageIcon,
  subCoverageTextColor,
} from "../../utils/healthCoverageAnalysisUtil";

interface Props {
  data: HealthCoverageAnalysis;
  layerGroups: HealthCoverageLayerGroup[];
}

interface PolicyMeta {
  policy_number: string;
  handler_code: string;
  plan_classification: string | null | undefined;
  premium_total: number;
}

function extractPolicies(data: HealthCoverageAnalysis): PolicyMeta[] {
  const seen = new Map<string, PolicyMeta>();
  const allItems: HealthCoverageItem[] = [];

  for (const cat of Object.values(data.categories)) {
    for (const sub of cat.sub_coverages ?? []) {
      allItems.push(...sub.items);
    }
  }
  allItems.push(...(data.unclassified_items ?? []));

  for (const item of allItems) {
    if (item.policy_number && !seen.has(item.policy_number)) {
      seen.set(item.policy_number, {
        policy_number: item.policy_number,
        handler_code: item.handler_code,
        plan_classification: item.plan_classification,
        premium_total: data.policy_premiums?.[item.policy_number] ?? 0,
      });
    }
  }

  return Array.from(seen.values())
    .sort((a, b) => a.policy_number.localeCompare(b.policy_number));
}

const POLICY_COL_WIDTH = 160;

function getLabelColWidth(policyCount: number): number {
  return policyCount > 3 ? 260 : 440;
}

function stickyLabelSx(labelColWidth: number) {
  return {
    position: "sticky" as const,
    left: 0,
    zIndex: 2,
    width: labelColWidth,
    minWidth: labelColWidth,
    borderRight: "1px solid #e0e0e0",
    textAlign: "right" as const,
  };
}

const HealthInsuranceCompareTable: React.FC<Props> = ({ data, layerGroups }) => {
  const policies = extractPolicies(data);

  if (policies.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        אין נתוני פוליסות להשוואה
      </Typography>
    );
  }

  const labelColWidth = getLabelColWidth(policies.length);
  const STICKY_LABEL_SX = stickyLabelSx(labelColWidth);
  return (
    <Box sx={{ overflowX: "auto" }} dir="rtl">
      <Table size="small" sx={{ tableLayout: "fixed", width: labelColWidth + policies.length * POLICY_COL_WIDTH }} dir="rtl">

        {/* ── שורת כותרות פוליסות — פעם אחת בלבד ── */}
        <TableHead>
          <TableRow sx={{ bgcolor: "#f0f4f8" }}>
            <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: "#f0f4f8", borderBottom: "1px solid", borderColor: "divider", fontWeight: "bold" }}>
              כיסוי
            </TableCell>
            {policies.map((p) => (
              <TableCell key={p.policy_number} align="center"
                sx={{ width: POLICY_COL_WIDTH, minWidth: POLICY_COL_WIDTH, borderBottom: "1px solid", borderColor: "divider", verticalAlign: "top", py: 1 }}>
                <Stack spacing={0.5} alignItems="center">
                  {p.plan_classification ? (
                    <Chip
                      label={formatPlanClassification(p.plan_classification as PlanClassification)}
                      size="small"
                      color={
                        p.plan_classification === PlanClassification.GROUP ||
                        p.plan_classification === PlanClassification.KUPAT_HOLIM_GROUP ? "info" : "default"
                      }
                      variant="outlined"
                    />
                  ) : <Box />}
                  <Typography variant="body2" fontWeight={600} sx={{ direction: "ltr" }}>
                    {formatHandlerCode(p.handler_code)}
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ direction: "ltr" }}>
                    {p.policy_number}
                  </Typography>
                </Stack>
              </TableCell>
            ))}
          </TableRow>

          {/* ── שורת עלות ── */}
          <TableRow sx={{ bgcolor: "#ffffff" }}>
            <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: "#ffffff", borderBottom: "2px solid", borderColor: "divider" }}>
              <Typography variant="body2" fontWeight={700}>עלות</Typography>
            </TableCell>
            {policies.map((p) => (
              <TableCell key={p.policy_number} align="center"
                sx={{ borderBottom: "2px solid", borderColor: "divider", py: 0.75, bgcolor: "#ffffff" }}>
                {p.premium_total > 0 ? (
                  <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(p.premium_total)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled">—</Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {layerGroups.map((group) => (
            <React.Fragment key={group.layer}>

              {/* ── כותרת שכבה ── */}
              <TableRow>
                <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: "#ffffff", py: 0.75, px: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Chip
                    label={group.label}
                    sx={{
                      bgcolor: theme.palette.brand.purpleLight,
                      color: theme.palette.brand.orange,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      borderRadius: "8px",
                    }}
                  />
                </TableCell>
                {policies.map((p) => (
                  <TableCell key={p.policy_number} sx={{ bgcolor: "#ffffff", borderBottom: "1px solid", borderColor: "divider" }} />
                ))}
              </TableRow>

              {group.rows.map((row) => (
                <React.Fragment key={row.categoryKey}>

                  {/* ── כותרת קטגוריה ── */}
                  <TableRow sx={{ bgcolor: theme.palette.brand.purpleLight }}>
                    <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: theme.palette.brand.purpleLight,
                      borderBottom: "1px solid", borderColor: "divider", py: 0.75 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" fontWeight="bold">{row.label}</Typography>
                        <Chip label={row.statusLabel} size="small"
                          color={row.overallStatus === "HAS" ? "success" : row.statusColor}
                          variant="filled" />
                      </Box>
                    </TableCell>
                    {policies.map((p) => {
                      return (
                        <TableCell key={p.policy_number}
                          sx={{ borderBottom: "1px solid", borderColor: "divider",
                            bgcolor: theme.palette.brand.purpleLight }} />
                      );
                    })}
                  </TableRow>

                  {/* ── שורות sub-coverage ── */}
                  {row.subCoverages.filter((sub) => !(sub.status !== "HAS" && sub.is_optional)).map((sub) => {
                    const riskLabel = formatDuplicateRisk(sub.duplicate_risk);
                    return (
                      <TableRow key={sub.key} hover>
                        <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: "inherit", borderBottom: "1px solid", borderColor: "divider", py: 0.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, pr: 1 }}>
                            {sub.status === "HAS"
                              ? <CheckCircleIcon fontSize="small" sx={{ color: "success.main", flexShrink: 0 }} />
                              : <span style={{ flexShrink: 0 }}>{subCoverageIcon(sub.status, sub.importance)}</span>
                            }
                            <Typography variant="body2" color={subCoverageTextColor(sub.status, sub.importance)}
                              sx={{ fontStyle: "normal" }}>
                              {sub.label}
                            </Typography>
                            {riskLabel && (
                              <Typography variant="body2" color="warning.dark" sx={{ flexShrink: 0 }}>⚠ {riskLabel}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        {policies.map((p) => {
                          const policyItems = sub.items.filter((i) => i.policy_number === p.policy_number);
                          return (
                            <TableCell key={p.policy_number} align="center"
                              sx={{ borderBottom: "1px solid", borderColor: "divider", py: 0.5, verticalAlign: policyItems.length > 0 ? "top" : "middle" }}>
                              {policyItems.length > 0 ? (
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                  <CheckCircleIcon fontSize="small" sx={{ color: "success.main", mt: 1 }} />
                                  <Box sx={{ mt: 1 }}>
                                    {[...new Map(policyItems.map((i) => [i.raw_value, i])).values()].map((item) => {
                                      const coverageSum = policyItems
                                        .filter((i) => i.raw_value === item.raw_value)
                                        .reduce((sum, i) => sum + (i.coverage_sum ?? 0), 0);
                                      return (
                                        <Box key={item.raw_value} sx={{ textAlign: "center" }}>
                                          <Typography variant="body2" color="text.secondary">
                                            {formatCoverageItemLabel(item.raw_value) || item.raw_value}
                                          </Typography>
                                          {coverageSum > 0 && (
                                            <Typography variant="body2" color="text.primary">
                                              <strong>כיסוי: {formatCurrency(coverageSum)}</strong>
                                            </Typography>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}

                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
          {/* ── ללא שיוך ── */}
          {(data.unclassified_items?.length ?? 0) > 0 && (
            <>
              <TableRow>
                <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: "#ffffff", py: 0.75, px: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Chip
                    label="ללא שיוך"
                    sx={{
                      bgcolor: theme.palette.brand.purpleLight,
                      color: theme.palette.brand.orange,
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      borderRadius: "8px",
                    }}
                  />
                </TableCell>
                {policies.map((p) => (
                  <TableCell key={p.policy_number} sx={{ bgcolor: "#ffffff", borderBottom: "1px solid", borderColor: "divider" }} />
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...STICKY_LABEL_SX, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">כיסויים לא מזוהים</Typography>
                </TableCell>
                {policies.map((p) => {
                  const items = data.unclassified_items.filter((i) => i.policy_number === p.policy_number);
                  return (
                    <TableCell key={p.policy_number} align="center"
                      sx={{ borderBottom: "1px solid", borderColor: "divider", py: 0.5, verticalAlign: items.length > 0 ? "top" : "middle" }}>
                      {items.length > 0 ? (
                        <Stack spacing={0.5} alignItems="center">
                          {items.map((item, i) => (
                            <Typography key={i} variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                              {formatCoverageItemLabel(item.raw_value) || item.raw_value}
                            </Typography>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default HealthInsuranceCompareTable;
